import { secureImageUrl } from "services/utils/payslipTemplate";
import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  Font,
} from "@react-pdf/renderer";
import moment from "moment";
import Roboto from "../../../assets/fonts/Roboto/Roboto-Regular.ttf";
import RobotoBold from "../../../assets/fonts/Roboto/Roboto-Bold.ttf";
import { normalizeBandName } from "services/utils/bandLabel";
import {
  formatQuantity,
  formatRate,
  getComponentQuantity,
  getComponentRate,
} from "./otherEarningsFormat";

Font.register({
  family: "Roboto",
  fonts: [{ src: Roboto }, { src: RobotoBold, fontWeight: 600 }],
  format: "truetype",
});

const BORDER = "1px solid #000";

// Column flex weights shared across every section so the grid lines up.
// Every vertical rule on the sheet falls on one of these four boundaries.
const C_LNAME = 2.7; // earnings / left label
const C_LAMT = 1.5; // earnings amount
const C_RNAME = 2.7; // deductions / right label
const C_RAMT = 1.5; // deductions amount
const C_TOTAL = C_LNAME + C_LAMT + C_RNAME + C_RAMT;

// Other Earnings table: Component | Qty | Rate | Total.
//
// Component, Qty and Rate subdivide the EARNINGS column and nothing else, so
// Total occupies the whole AMOUNT column - each Other Earnings amount then
// prints in the same column as Basic/DA/HRA above it and as Total Earnings(A)
// below it. The block adds two inner rules inside EARNINGS and none anywhere
// else on the sheet.
const OE_C_NAME = 1.5;
const OE_C_QTY = 0.6;
const OE_C_RATE = C_LNAME - OE_C_NAME - OE_C_QTY; // 0.6
const OE_C_TOTAL = C_LAMT;

// OT details table: Band Name | Earnings | Deductions | Net. Three of its four
// rules continue rules already running down the sheet - the label ends on the
// EARNINGS/AMOUNT rule, Band Name on the AMOUNT/DEDUCTIONS rule, and Net on the
// DEDUCTIONS/AMOUNT rule that runs down the attendance rows above it.
const OT_C_LABEL = C_LNAME;
const OT_C_BAND = C_LAMT;
const OT_C_NET = C_RAMT;
const OT_C_VALUE = C_RNAME / 2;
// The OT band table is nested inside the row, so its columns are a share of
// what is left beside the label rather than of the whole sheet.
const OT_SUB_TOTAL = C_TOTAL - OT_C_LABEL;

// Cells are sized by percentage width, NOT by `flex`.
//
// react-pdf subtracts every cell's border from the space `flex` has left to
// share out, so a row of four cells and a row of two cells put their
// boundaries at different x - by a point or three. Stacked up the sheet that
// is what makes the rules look broken and doubled. A percentage width is
// border-box, so a boundary lands on the same x in every row regardless of how
// many cells that row is divided into.
const col = (units, total = C_TOTAL) => ({
  width: `${((units / total) * 100).toFixed(4)}%`,
});

const styles = StyleSheet.create({
  page: {
    padding: 24,
    fontFamily: "Roboto",
    color: "#000",
    fontSize: 8,
  },
  outer: {
    borderTop: BORDER,
    borderLeft: BORDER,
  },
  row: {
    flexDirection: "row",
  },
  // Company header (single full-width merged cell)
  headerCell: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    borderRight: BORDER,
    borderBottom: BORDER,
    padding: 4,
  },
  logoContainer: {
    width: 55,
    marginRight: 6,
  },
  logo: {
    width: 50,
    height: 28,
    objectFit: "contain",
  },
  companyInfo: {
    flex: 1,
    alignItems: "center",
  },
  companyName: {
    fontSize: 11,
    fontWeight: 600,
  },
  companyAddress: {
    fontSize: 8,
    marginTop: 1,
    textAlign: "center",
  },
  titleText: {
    fontSize: 9,
    fontWeight: 600,
    marginTop: 2,
  },
  monthText: {
    fontSize: 8.5,
    fontWeight: 600,
    marginTop: 1,
  },
  // Generic bordered cell
  cell: {
    borderRight: BORDER,
    borderBottom: BORDER,
    paddingVertical: 2,
    paddingHorizontal: 3,
    justifyContent: "center",
  },
  headCell: {
    borderRight: BORDER,
    borderBottom: BORDER,
    paddingVertical: 2,
    paddingHorizontal: 3,
    backgroundColor: "#e6e6e6",
    justifyContent: "center",
  },
  // Employee identity block: a label column of fixed width so the colons line
  // up down each half, with the value taking whatever is left.
  identityCell: {
    padding: 4,
    justifyContent: "flex-start",
  },
  identityLine: {
    flexDirection: "row",
    marginBottom: 2,
  },
  identityLabel: {
    fontSize: 8,
    fontWeight: 600,
    width: 85,
  },
  identityValue: {
    fontSize: 8,
    flex: 1,
  },
  text: {
    fontSize: 8,
  },
  textBold: {
    fontSize: 8,
    fontWeight: 600,
  },
  amount: {
    fontSize: 8,
    textAlign: "right",
  },
  amountBold: {
    fontSize: 8,
    fontWeight: 600,
    textAlign: "right",
  },
  center: {
    textAlign: "center",
  },
  left: {
    textAlign: "left",
  },
  right: {
    textAlign: "right",
  },
  // Every column on the sheet is set the same way: names and labels read from
  // the left, figures line up on the right, and a column heading takes the
  // alignment of the column under it. Only the two section bands that span the
  // full width ("Attendance Details", "OT Details") stay centred.
  otHeadText: {
    fontSize: 6.5,
    fontWeight: 600,
    textAlign: "right",
  },
  // Other Earnings rows sit in three narrow columns inside EARNINGS, so they
  // are set a little smaller to keep a name like "2 OT Weekoff Attendance" on
  // one line.
  oeHeadText: {
    fontSize: 7,
    fontWeight: 600,
    textAlign: "right",
  },
  oeText: {
    fontSize: 7.5,
  },
  oeAmount: {
    fontSize: 7.5,
    textAlign: "right",
  },
  otValueText: {
    fontSize: 7,
    textAlign: "right",
  },
  otValueBoldText: {
    fontSize: 7,
    fontWeight: 600,
    textAlign: "right",
  },
  note: {
    marginTop: 6,
    fontSize: 7.5,
  },
});

const isEmpty = (value) =>
  value === undefined || value === null || value === "";

const formatText = (value) => (isEmpty(value) ? "" : value);

const toNumber = (value) => {
  if (isEmpty(value)) {
    return null;
  }
  const parsed = parseFloat(`${value}`.replace(/,/g, ""));
  return Number.isNaN(parsed) ? null : parsed;
};

// Indian-style grouped amount, e.g. 22550 -> "22,550". Falls back to "-".
const formatAmount = (value, decimals = 0) => {
  const parsed = toNumber(value);
  if (parsed === null) {
    return "-";
  }
  return parsed.toLocaleString("en-IN", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const formatMonth = (payslipData) => {
  const reference = payslipData?.pay_date || payslipData?.start_date;
  const parsed = moment(
    reference,
    ["DD-MM-YYYY", "YYYY-MM-DD", "DD/MM/YYYY", moment.ISO_8601],
    true,
  );
  return parsed.isValid()
    ? parsed.format("MMMM -YYYY").toUpperCase()
    : formatText(payslipData?.pay_schedule_name);
};

const pickNumber = (...values) => {
  for (const value of values) {
    const parsed = toNumber(value);
    if (parsed !== null) {
      return parsed;
    }
  }
  return null;
};

const getComponentName = (item) =>
  formatText(
    item?.component_name ?? item?.name ?? item?.component ?? item?.label,
  );

// An Other Earnings row not mapped to a configured OT band shows the
// backend's raw "Default <band>" placeholder: "Default Band" for plain
// overtime, "Default Week Off" / "Default Holiday" for those categories
// (e.g. "Default Band Overtime", "Default Week Off Weekoff Attendance",
// "Default Holiday Holiday Attendance") — every one of them is the same
// unconfigured/factor-1 band and reads as "1 OT".
const DEFAULT_BAND_LABEL_PATTERN = /^Default\s+(?:Band|Week\s*Off|Holiday)\b/i;
const getOtherEarningName = (item) => {
  const name = getComponentName(item);
  return typeof name === "string" && DEFAULT_BAND_LABEL_PATTERN.test(name)
    ? name.replace(DEFAULT_BAND_LABEL_PATTERN, "1 OT").replace(/\s+/g, " ").trim()
    : name;
};

const getComponentAmount = (item) =>
  item?.amount ?? item?.component_amount ?? item?.value ?? item?.total;

const normalizeComponentKey = (value) =>
  `${value || ""}`.toLowerCase().replace(/[^a-z0-9]/g, "");

const normalizeItems = (...sections) =>
  sections
    .flatMap((section) => (Array.isArray(section) ? section : []))
    .filter((item) => item && getComponentName(item));

const sumComponentAmounts = (items) =>
  items.reduce((sum, item) => sum + (toNumber(getComponentAmount(item)) || 0), 0);

const EARNING_ROWS = [
  { label: "Basic", keys: ["Basic"] },
  { label: "DA", keys: ["DA", "Dearness Allowance"] },
  { label: "HRA", keys: ["HRA", "House Rent Allowance"] },
  { label: "Leave Salary", keys: ["Leave Salary", "Leave Wages"] },
  {
    label: "Nightshift Allow.",
    keys: ["Nightshift Allow.", "Night Shift Allowance", "Nightshift Allowance"],
  },
  { label: "Sunday Allow.", keys: ["Sunday Allow.", "Sunday Allowance"] },
  { label: "OT", keys: ["OT", "OT Wages", "Overtime", "Over Time"] },
  {
    label: "Attendance Bonus",
    keys: ["Attendance Bonus"],
    fallbackAmount: 0,
    decimals: 2,
  },
  {
    label: "Mobile Charges",
    keys: ["Mobile Charges", "Mobile Allowance"],
    fallbackAmount: 0,
    decimals: 2,
  },
];

const DEDUCTION_ROWS = [
  { label: "PF", keys: ["PF", "P.F", "P.F.", "Provident Fund"] },
  { label: "ESI", keys: ["ESI", "E.S.I", "E.S.I.", "Employee State Insurance"] },
  { label: "Lunch", keys: ["Lunch", "Lunch Deduction"] },
  { label: "Salary Advance", keys: ["Salary Advance", "Advance"] },
  { label: "PT", keys: ["PT", "Professional Tax"] },
  { label: "LWF", keys: ["LWF", "Labour Welfare Fund"] },
];

// Build the rows to print for one column (earnings or deductions).
//
// Only components that are actually present in the payslip response are
// rendered. Known statutory components are relabelled to their canonical
// Form XIX label and kept in the statutory order; any other component keeps
// its own name and is appended afterwards. Statutory rows that have no
// matching component are NOT injected, so a real payslip never shows empty
// "-" rows for components the employee does not have.
const buildDisplayRows = (items, statutoryRows) => {
  const configByKey = new Map();
  statutoryRows.forEach((row, order) => {
    row.keys.forEach((key) => {
      const normalized = normalizeComponentKey(key);
      if (normalized && !configByKey.has(normalized)) {
        configByKey.set(normalized, { row, order });
      }
    });
  });

  const rows = [];
  const usedKeys = new Set();

  items.forEach((item, index) => {
    const rawName = getComponentName(item);
    const key = normalizeComponentKey(rawName);
    if (!key || usedKeys.has(key)) {
      return;
    }
    usedKeys.add(key);

    const config = configByKey.get(key);
    if (config) {
      rows.push({
        ...item,
        component_name: config.row.label,
        fallbackAmount: config.row.fallbackAmount ?? "-",
        decimals: config.row.decimals ?? 0,
        _order: config.order,
      });
    } else {
      rows.push({
        ...item,
        component_name: rawName,
        fallbackAmount: "-",
        decimals: 0,
        _order: statutoryRows.length + index,
      });
    }
  });

  return rows.sort((a, b) => a._order - b._order);
};

const formatComponentAmount = (item) => {
  const amount = getComponentAmount(item);
  if (toNumber(amount) !== null) {
    return formatAmount(amount, item?.decimals || 0);
  }

  if (typeof item?.fallbackAmount === "number") {
    return formatAmount(item.fallbackAmount, item?.decimals || 0);
  }

  return item?.fallbackAmount || "-";
};

// Band values arrive as HH:MM strings ("66:00", "- 80:00", "—"). Mirror the
// Attendance side peek: print what the pay run reported, and show "-" when a
// band carries no value for that column.
const formatBandValue = (value) => (isEmpty(value) ? "-" : `${value}`.trim());

// HH:MM <-> minutes, so the Grand Total row adds up the same way the
// "Net earnings by band" table in the side peek does.
const parseTimeMinutes = (value) => {
  if (typeof value !== "string") return 0;

  const match = value.trim().match(/^(-)?\s*(\d+):(\d{2})$/);
  if (!match) return 0;

  const [, negativeSign, hours, minutes] = match;
  return (negativeSign ? -1 : 1) * (Number(hours) * 60 + Number(minutes));
};

const formatTimeMinutes = (value) => {
  if (!Number.isFinite(value)) return "00:00";

  const sign = value < 0 ? "-" : "";
  const absoluteValue = Math.abs(value);
  const hours = Math.floor(absoluteValue / 60);
  const minutes = absoluteValue % 60;

  return `${sign}${String(hours).padStart(2, "0")}:${String(minutes).padStart(
    2,
    "0",
  )}`;
};

// OT details are driven entirely by `net_earnings_by_band` on the payslip
// response -- whichever bands the pay run reported are the rows that print, in
// the order the response lists them. "Attendance" is the base shift band rather
// than an OT band, so it is left out here exactly as the side peek does.
const buildOtBandRows = (payslipData) => {
  const bands = payslipData?.net_earnings_by_band;
  if (!bands || typeof bands !== "object" || Array.isArray(bands)) {
    return [];
  }

  return Object.entries(bands)
    .filter(([bandName]) => bandName !== "Attendance")
    .map(([bandName, bandData]) => ({
      // The unconfigured band comes back from the BE as either "Default
      // Band" or a category-specific variant ("Default Week Off" /
      // "Default Holiday") — this is the same bare band-name column the
      // Attendance side-peek's Band Name table shows, so it should read the
      // same canonical "Default Band" there too, never the category variant.
      bandName: normalizeBandName(bandName),
      earnings: bandData?.earning,
      deductions: bandData?.total_deducted,
      net: bandData?.net_earnings,
    }));
};

// Draw one row's earnings half. A plain component row keeps the two-column
// EARNINGS | AMOUNT split; the Other Earnings rows subdivide the same width
// into Component | Qty | Rate | Total; a missing row leaves the half blank so
// the grid still closes when the deductions side is longer.
const renderLeftHalf = (left, totalOtherEarnings) => {
  if (left?.kind === "otherHead") {
    return (
      <>
        <View style={[styles.headCell, col(OE_C_NAME)]}>
          <Text style={[styles.oeHeadText, styles.left]}>OTHER EARNINGS</Text>
        </View>
        <View style={[styles.headCell, col(OE_C_QTY)]}>
          <Text style={styles.oeHeadText}>QTY</Text>
        </View>
        <View style={[styles.headCell, col(OE_C_RATE)]}>
          <Text style={styles.oeHeadText}>RATE</Text>
        </View>
        <View style={[styles.headCell, col(OE_C_TOTAL)]}>
          <Text style={[styles.textBold, styles.right]}>TOTAL</Text>
        </View>
      </>
    );
  }

  if (left?.kind === "other") {
    return (
      <>
        <View style={[styles.cell, col(OE_C_NAME)]}>
          <Text style={styles.oeText}>
            {getOtherEarningName(left.item)}
          </Text>
        </View>
        <View style={[styles.cell, col(OE_C_QTY)]}>
          <Text style={styles.oeAmount}>
            {formatQuantity(getComponentQuantity(left.item))}
          </Text>
        </View>
        <View style={[styles.cell, col(OE_C_RATE)]}>
          <Text style={styles.oeAmount}>
            {formatRate(getComponentRate(left.item))}
          </Text>
        </View>
        <View style={[styles.cell, col(OE_C_TOTAL)]}>
          <Text style={styles.amount}>
            {formatAmount(getComponentAmount(left.item), 2)}
          </Text>
        </View>
      </>
    );
  }

  if (left?.kind === "otherTotal") {
    return (
      <>
        <View style={[styles.cell, col(C_LNAME)]}>
          <Text style={styles.textBold}>Total Other Earnings</Text>
        </View>
        <View style={[styles.cell, col(C_LAMT)]}>
          <Text style={styles.amountBold}>
            {formatAmount(totalOtherEarnings, 2)}
          </Text>
        </View>
      </>
    );
  }

  return (
    <>
      <View style={[styles.cell, col(C_LNAME)]}>
        <Text style={styles.text}>
          {left ? formatText(left.item.component_name) : " "}
        </Text>
      </View>
      <View style={[styles.cell, col(C_LAMT)]}>
        <Text style={styles.amount}>
          {left ? formatComponentAmount(left.item) : " "}
        </Text>
      </View>
    </>
  );
};

export const FormXixWageSlipPage = ({ payslipData }) => {
  if (!payslipData) {
    return null;
  }

  const earningItems = normalizeItems(payslipData?.earnings);
  const otherEarningItems = normalizeItems(payslipData?.other_earnings);
  const deductionItems = normalizeItems(
    payslipData?.deduction,
    payslipData?.deductions,
  );
  const otherDeductionItems = normalizeItems(
    payslipData?.other_deduction,
    payslipData?.other_deductions,
    payslipData?.additional_deduction,
    payslipData?.additional_deductions,
  );

  // Other Earnings are NOT merged into the EARNINGS column - they print in
  // their own table below it, with the Qty/Rate that produced each amount.
  const earnings = buildDisplayRows(earningItems, EARNING_ROWS);
  const deductions = buildDisplayRows(
    [...deductionItems, ...otherDeductionItems],
    DEDUCTION_ROWS,
  );

  const totalOtherEarnings =
    pickNumber(payslipData?.total_other_earnings) ??
    sumComponentAmounts(otherEarningItems);
  const totalEarnings =
    (pickNumber(payslipData?.total_earnings) ??
      sumComponentAmounts(earningItems)) + totalOtherEarnings;
  const totalDeductions =
    (pickNumber(payslipData?.total_deductions, payslipData?.total_deduction) ??
      sumComponentAmounts(deductionItems)) +
    (pickNumber(
      payslipData?.total_other_deductions,
      payslipData?.total_other_deduction,
      payslipData?.total_additional_deductions,
    ) ?? sumComponentAmounts(otherDeductionItems));

  const workingDays = pickNumber(
    payslipData?.total_payable_shift,
    payslipData?.working_days,
    payslipData?.total_working_days,
    payslipData?.number_of_days_working_for_month,
  );
  const daysWorked = pickNumber(
    payslipData?.net_payable_shift,
    payslipData?.days_worked,
    payslipData?.number_of_days_worked,
  );
  const explicitLopDays = pickNumber(
    payslipData?.lop_days,
    payslipData?.lop_days_count,
    payslipData?.loss_of_pay_days,
  );
  const lopDays =
    explicitLopDays !== null
      ? explicitLopDays
      : workingDays !== null && daysWorked !== null
        ? Math.max(workingDays - daysWorked, 0)
        : null;
  const netPay =
    pickNumber(payslipData?.net_pay, payslipData?.net_salary) ??
    Math.max(totalEarnings - totalDeductions, 0);
  const otBandRows = buildOtBandRows(payslipData);
  const otTotals = otBandRows.reduce(
    (total, row) => ({
      earnings: total.earnings + parseTimeMinutes(row.earnings),
      deductions: total.deductions + parseTimeMinutes(row.deductions),
      net: total.net + parseTimeMinutes(row.net),
    }),
    { earnings: 0, deductions: 0, net: 0 },
  );

  // The two halves are filled independently: the left one carries the earnings
  // components followed by the Other Earnings block, the right one the
  // deductions. Rows are then printed side by side and the shorter half runs
  // out into blank cells, so the grid closes without either block bleeding
  // across the middle rule.
  const leftRows = [
    ...earnings.map((item) => ({ kind: "component", item })),
    ...(otherEarningItems.length
      ? [
          { kind: "otherHead" },
          ...otherEarningItems.map((item) => ({ kind: "other", item })),
          { kind: "otherTotal" },
        ]
      : []),
  ];
  const rightRows = deductions.map((item) => ({ kind: "component", item }));

  const rowCount = Math.max(leftRows.length, rightRows.length);
  const bodyRows = Array.from({ length: rowCount });

  return (
    <Page size="A4" style={styles.page}>
      <View style={styles.outer}>
        {/* ---- Company header (full-width merged cell) ---- */}
        <View style={styles.row}>
          <View style={styles.headerCell}>
            {payslipData?.company_logo_url ? (
              <View style={styles.logoContainer}>
                <Image style={styles.logo} src={secureImageUrl(payslipData.company_logo_url)} />
              </View>
            ) : null}
            <View style={styles.companyInfo}>
              <Text style={styles.companyName}>
                {payslipData?.company_name || "Organflow"}
              </Text>
              {payslipData?.company_address ? (
                <Text style={styles.companyAddress}>
                  {payslipData?.company_address}
                </Text>
              ) : null}
              <Text style={styles.titleText}>Wage slip - Form - XIX</Text>
              <Text style={styles.monthText}>
                Wage slip for the month of {formatMonth(payslipData)}
              </Text>
            </View>
          </View>
        </View>

        {/* ---- Employee identity block ----
             Two half-width cells of "Label : value" lines, the same block the
             Rule 27(2) wage slip prints. The halves split on the
             EARNINGS/DEDUCTIONS boundary (C_LNAME + C_LAMT), so the one rule it
             draws continues a rule already running down the sheet. */}
        <View style={styles.row}>
          <View style={[styles.cell, styles.identityCell, col(C_LNAME + C_LAMT)]}>
            <View style={styles.identityLine}>
              <Text style={styles.identityLabel}>Employee Code</Text>
              <Text style={styles.identityValue}>
                : {formatText(payslipData?.employee_id)}
              </Text>
            </View>
            <View style={styles.identityLine}>
              <Text style={styles.identityLabel}>Employee Name</Text>
              <Text style={styles.identityValue}>
                : {formatText(payslipData?.user_name)}
              </Text>
            </View>
          </View>
          <View style={[styles.cell, styles.identityCell, col(C_RNAME + C_RAMT)]}>
            <View style={styles.identityLine}>
              <Text style={styles.identityLabel}>Designation</Text>
              <Text style={styles.identityValue}>
                : {formatText(payslipData?.user_designation)}
              </Text>
            </View>
            <View style={styles.identityLine}>
              <Text style={styles.identityLabel}>Department</Text>
              <Text style={styles.identityValue}>
                : {formatText(payslipData?.department_name)}
              </Text>
            </View>
          </View>
        </View>
        <View style={styles.row}>
          <View style={[styles.cell, styles.identityCell, col(C_LNAME + C_LAMT)]}>
            <View style={styles.identityLine}>
              <Text style={styles.identityLabel}>PF No.</Text>
              <Text style={styles.identityValue}>
                : {formatText(payslipData?.pf_no)}
              </Text>
            </View>
          </View>
          <View style={[styles.cell, styles.identityCell, col(C_RNAME + C_RAMT)]}>
            <View style={styles.identityLine}>
              <Text style={styles.identityLabel}>ESI No.</Text>
              <Text style={styles.identityValue}>
                : {formatText(payslipData?.esi_no)}
              </Text>
            </View>
          </View>
        </View>

        {/* ---- Wages per month ---- */}
        <View style={styles.row}>
          <View style={[styles.cell, col(C_LNAME)]}>
            <Text style={styles.textBold}>Wages per month</Text>
          </View>
          <View style={[styles.cell, col(C_LAMT)]}>
            <Text style={styles.amount}>
              {formatAmount(payslipData?.monthly_salary)}
            </Text>
          </View>
          <View style={[styles.cell, col(C_RNAME + C_RAMT)]}>
            <Text style={styles.text}> </Text>
          </View>
        </View>

        {/* ---- Earnings / Deductions header ---- */}
        <View style={styles.row}>
          <View style={[styles.headCell, col(C_LNAME)]}>
            <Text style={styles.textBold}>EARNINGS</Text>
          </View>
          <View style={[styles.headCell, col(C_LAMT)]}>
            <Text style={[styles.textBold, styles.right]}>AMOUNT</Text>
          </View>
          <View style={[styles.headCell, col(C_RNAME)]}>
            <Text style={styles.textBold}>DEDUCTIONS</Text>
          </View>
          <View style={[styles.headCell, col(C_RAMT)]}>
            <Text style={[styles.textBold, styles.right]}>AMOUNT</Text>
          </View>
        </View>

        {/* ---- Earnings (+ Other Earnings) / Deductions rows ----
             Each printed row draws its left half from `leftRows` and its right
             half from `rightRows`. The Other Earnings block therefore splits
             only the earnings half into Component | Qty | Rate | Total while
             the deductions column beside it stays whole. */}
        {bodyRows.map((_, index) => {
          const left = leftRows[index];
          const right = rightRows[index];
          return (
            <View style={styles.row} key={index}>
              {renderLeftHalf(left, totalOtherEarnings)}
              <View style={[styles.cell, col(C_RNAME)]}>
                <Text style={styles.text}>
                  {right ? formatText(right.item.component_name) : " "}
                </Text>
              </View>
              <View style={[styles.cell, col(C_RAMT)]}>
                <Text style={styles.amount}>
                  {right ? formatComponentAmount(right.item) : " "}
                </Text>
              </View>
            </View>
          );
        })}

        {/* ---- Totals ---- */}
        <View style={styles.row}>
          <View style={[styles.headCell, col(C_LNAME)]}>
            <Text style={styles.textBold}>Total Earnings(A)</Text>
          </View>
          <View style={[styles.headCell, col(C_LAMT)]}>
            <Text style={styles.amountBold}>{formatAmount(totalEarnings)}</Text>
          </View>
          <View style={[styles.headCell, col(C_RNAME)]}>
            <Text style={styles.textBold}>Total Deductions(B)</Text>
          </View>
          <View style={[styles.headCell, col(C_RAMT)]}>
            <Text style={styles.amountBold}>{formatAmount(totalDeductions)}</Text>
          </View>
        </View>

        {/* ---- Blank band + Net Salary Paid on the right ---- */}
        <View style={styles.row}>
          <View style={[styles.cell, col(C_LNAME + C_LAMT)]}>
            <Text style={styles.text}> </Text>
          </View>
          <View style={[styles.headCell, col(C_RNAME)]}>
            <Text style={styles.textBold}>Net Salary Paid(A-B)</Text>
          </View>
          <View style={[styles.headCell, col(C_RAMT)]}>
            <Text style={styles.amountBold}>{formatAmount(netPay)}</Text>
          </View>
        </View>

        {/* ---- Attendance details ---- */}
        <View style={styles.row}>
          <View
            style={[
              styles.headCell,
              col(C_LNAME + C_LAMT + C_RNAME),
            ]}
          >
            <Text style={[styles.textBold, styles.center]}>
              Attendance Details
            </Text>
          </View>
          <View style={[styles.headCell, col(C_RAMT)]}>
            <Text style={[styles.textBold, styles.right]}>Days</Text>
          </View>
        </View>
        <View style={styles.row}>
          <View
            style={[styles.cell, col(C_LNAME + C_LAMT + C_RNAME)]}
          >
            <Text style={styles.text}>Number of days working for the month</Text>
          </View>
          <View style={[styles.cell, col(C_RAMT)]}>
            <Text style={styles.amount}>
              {workingDays === null ? "-" : formatAmount(workingDays)}
            </Text>
          </View>
        </View>
        <View style={styles.row}>
          <View
            style={[styles.cell, col(C_LNAME + C_LAMT + C_RNAME)]}
          >
            <Text style={styles.text}>LOP Days</Text>
          </View>
          <View style={[styles.cell, col(C_RAMT)]}>
            <Text style={styles.amount}>
              {lopDays === null ? "-" : formatAmount(lopDays, 1)}
            </Text>
          </View>
        </View>
        <View style={styles.row}>
          <View
            style={[styles.cell, col(C_LNAME + C_LAMT + C_RNAME)]}
          >
            <Text style={styles.text}>Number of days worked</Text>
          </View>
          <View style={[styles.cell, col(C_RAMT)]}>
            <Text style={styles.amount}>
              {daysWorked === null ? "-" : formatAmount(daysWorked, 1)}
            </Text>
          </View>
        </View>

        {/* ---- OT details (label spans the band rows) ---- */}
        {otBandRows.length > 0 ? (
          <View style={styles.row}>
            <View style={[styles.cell, col(OT_C_LABEL)]}>
              <Text style={[styles.textBold, styles.center]}>OT Details</Text>
            </View>
            <View style={col(C_TOTAL - OT_C_LABEL)}>
              <View style={styles.row}>
                <View style={[styles.headCell, col(OT_C_BAND, OT_SUB_TOTAL)]}>
                  <Text style={[styles.otHeadText, styles.left]}>Band Name</Text>
                </View>
                <View style={[styles.headCell, col(OT_C_VALUE, OT_SUB_TOTAL)]}>
                  <Text style={styles.otHeadText}>Earnings</Text>
                </View>
                <View style={[styles.headCell, col(OT_C_VALUE, OT_SUB_TOTAL)]}>
                  <Text style={styles.otHeadText}>Deductions</Text>
                </View>
                <View style={[styles.headCell, col(OT_C_NET, OT_SUB_TOTAL)]}>
                  <Text style={styles.otHeadText}>Net</Text>
                </View>
              </View>
              {otBandRows.map((row, idx) => (
                <View style={styles.row} key={`${row.bandName}-${idx}`}>
                  <View style={[styles.cell, col(OT_C_BAND, OT_SUB_TOTAL)]}>
                    <Text style={[styles.otValueText, styles.left]}>
                      {row.bandName}
                    </Text>
                  </View>
                  <View style={[styles.cell, col(OT_C_VALUE, OT_SUB_TOTAL)]}>
                    <Text style={styles.otValueText}>
                      {formatBandValue(row.earnings)}
                    </Text>
                  </View>
                  <View style={[styles.cell, col(OT_C_VALUE, OT_SUB_TOTAL)]}>
                    <Text style={styles.otValueText}>
                      {formatBandValue(row.deductions)}
                    </Text>
                  </View>
                  <View style={[styles.cell, col(OT_C_NET, OT_SUB_TOTAL)]}>
                    <Text style={styles.otValueText}>
                      {formatBandValue(row.net)}
                    </Text>
                  </View>
                </View>
              ))}
              <View style={styles.row}>
                <View style={[styles.cell, col(OT_C_BAND, OT_SUB_TOTAL)]}>
                  <Text style={[styles.otValueBoldText, styles.left]}>
                    Grand Total
                  </Text>
                </View>
                <View style={[styles.cell, col(OT_C_VALUE, OT_SUB_TOTAL)]}>
                  <Text style={styles.otValueBoldText}>
                    {formatTimeMinutes(otTotals.earnings)}
                  </Text>
                </View>
                <View style={[styles.cell, col(OT_C_VALUE, OT_SUB_TOTAL)]}>
                  <Text style={styles.otValueBoldText}>
                    {formatTimeMinutes(otTotals.deductions)}
                  </Text>
                </View>
                <View style={[styles.cell, col(OT_C_NET, OT_SUB_TOTAL)]}>
                  <Text style={styles.otValueBoldText}>
                    {formatTimeMinutes(otTotals.net)}
                  </Text>
                </View>
              </View>
            </View>
          </View>
        ) : null}
      </View>

      <Text style={styles.note}>
        Note:- This is a computer generated sheet which does not require
        signature.
      </Text>
    </Page>
  );
};

const FormXixWageSlipData = ({ payslipData }) => (
  <Document
    title={`${
      payslipData?.user_name ? payslipData?.user_name + " " : ""
    }${payslipData?.pay_schedule_name || "wage-slip-form-xix"}`}
  >
    <FormXixWageSlipPage payslipData={payslipData} />
  </Document>
);

export const BulkFormXixWageSlipData = ({ employeesPdfData = [] }) => (
  <Document>
    {employeesPdfData
      .filter((emp) => emp?.payslipData)
      .map((employee, index) => (
        <FormXixWageSlipPage key={index} payslipData={employee.payslipData} />
      ))}
  </Document>
);

export default FormXixWageSlipData;
